import { ReactNode, useId } from "react";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import { useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import CloseIcon from "@mui/icons-material/Close";
import { usePreferences } from "../../features/preferences/PreferencesProvider";

export type DialogSize = "sm" | "md" | "lg";
export type DialogVariant = "classic" | "light";
export const DIALOG_WIDTH: Record<DialogSize, number> = { sm: 520, md: 760, lg: 1180 };

export type AppDialogProps = {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  context?: ReactNode;
  size?: DialogSize;
  actions?: ReactNode;
  footerLeft?: ReactNode;
  busy?: boolean;
  variant?: DialogVariant;
  closeLabel?: string;
  children?: ReactNode;
};

export default function AppDialog({
  open,
  onClose,
  title,
  context,
  size = "sm",
  actions,
  footerLeft,
  busy = false,
  variant,
  closeLabel = "Fechar",
  children,
}: AppDialogProps) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const { prefs } = usePreferences();
  // A prop explícita vence a preferência do usuário (dialogStyle).
  const v: DialogVariant = variant ?? prefs.dialogStyle;
  const classic = v === "classic";
  const titleId = useId();
  // busy: backdrop, Esc e X não fecham.
  const close = () => {
    if (!busy) onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={close}
      fullScreen={fullScreen}
      maxWidth={false}
      aria-labelledby={titleId}
      data-variant={v}
      PaperProps={{
        sx: { width: "100%", maxWidth: fullScreen ? "100%" : DIALOG_WIDTH[size], borderRadius: fullScreen ? 0 : "12px" },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1,
          px: 3,
          pt: classic ? 2 : 2.5,
          pb: classic ? 1.75 : 1,
          borderBottom: classic ? `1px solid ${theme.palette.divider}` : "none",
        }}
      >
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            component="h2"
            id={titleId}
            sx={{ fontSize: classic ? 17 : 19, fontWeight: 700, color: "primary.main", lineHeight: 1.35 }}
          >
            {title}
          </Typography>
          {context &&
            (classic ? (
              <Typography data-testid="dialog-context" sx={{ fontSize: 13, color: "text.secondary", mt: 0.25 }}>
                {context}
              </Typography>
            ) : (
              <Chip
                data-testid="dialog-context"
                size="small"
                label={context}
                sx={{
                  mt: 0.75,
                  bgcolor: "#eef6f9",
                  color: "primary.main",
                  fontWeight: 500,
                  height: "auto",
                  maxWidth: "100%",
                  "& .MuiChip-label": { whiteSpace: "normal", py: "3px" },
                }}
              />
            ))}
        </Box>
        <IconButton
          aria-label={closeLabel}
          onClick={close}
          disabled={busy}
          sx={{ mt: -0.5, mr: -1, color: "text.secondary" }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>
      <Box sx={{ px: 3, py: classic ? 2.5 : 1.5, overflowY: "auto", flex: "1 1 auto" }}>{children}</Box>
      {(actions || footerLeft) && (
        <Box
          data-testid="dialog-actions"
          sx={{
            display: "flex",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 1,
            px: 3,
            py: classic ? 1.5 : 2,
            bgcolor: classic ? "background.default" : "transparent",
            borderTop: classic ? `1px solid ${theme.palette.divider}` : "none",
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>{footerLeft}</Box>
          {actions}
        </Box>
      )}
    </Dialog>
  );
}
